import {
  forwardRef,
  useCallback,
  useRef,
  useImperativeHandle,
  useLayoutEffect,
  useState,
  useEffect
} from 'react'

import { cn } from '@/utils'

import { Input, TInputProps } from '../Input'

import { editFromChange, editMoney, MoneyEdit } from './caret'

export type TMoneyInputProps = Omit<
  TInputProps,
  'type' | 'leftIcon' | 'value' | 'onChange' | 'onBlur'
> & {
  value?: number | string
  defaultValue?: number | string
  onChange?: (value: number | null) => void
  onBlur?: (value: number | null) => void
  allowNegative?: boolean
  maxValue?: number
  minValue?: number
}

/**
 * MoneyInput component for handling monetary values with USD formatting
 *
 * Features:
 * - Currency mask with right-to-left input (like a cash register)
 * - Automatic USD formatting with $ prefix and thousand separators ($1,234.56)
 * - Always displays 2 decimal places
 * - Type digits from right to left: "1" → "$0.01", "12" → "$0.12", "123" → "$1.23"
 * - Backspace removes digits from right to left
 * - Mid-value edits stay where the caret is; cents digits are overwritten
 * - Integrates with react-hook-form
 * - Returns numeric value (number) while displaying formatted string
 *
 * @example
 * ```tsx
 * // Uncontrolled
 * <MoneyInput placeholder="$0.00" />
 *
 * // Controlled
 * const [amount, setAmount] = useState(0)
 * <MoneyInput value={amount} onChange={setAmount} />
 *
 * // With react-hook-form
 * const { register } = useForm()
 * <MoneyInput {...register('amount', { valueAsNumber: true })} />
 *
 * // Typing behavior:
 * // Type "1" → displays "$0.01"
 * // Type "2" → displays "$0.12"
 * // Type "3" → displays "$1.23"
 * // Type "4" → displays "$12.34"
 * // Type "5" → displays "$123.45"
 * // Type "6" → displays "$1,234.56"
 * ```
 */
export const MoneyInput = forwardRef<HTMLInputElement, TMoneyInputProps>(
  (
    {
      value: propValue,
      defaultValue: propDefaultValue,
      onChange: propOnChange,
      onBlur: propOnBlur,
      onPaste: propOnPaste,
      onMouseDown: propOnMouseDown,
      allowNegative = false,
      maxValue,
      minValue,
      placeholder = '$0.00',
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    // Store value as cents (integer) to avoid floating point issues
    const [cents, setCents] = useState<number>(() => {
      const initial = propValue ?? propDefaultValue
      if (initial === null || initial === undefined || initial === '') return 0
      const numValue =
        typeof initial === 'string' ? parseFloat(initial) : initial
      return isNaN(numValue) ? 0 : Math.round(numValue * 100)
    })

    /**
     * Converts cents to formatted display string with $ prefix
     * Example: 12345 cents -> "$123.45"
     */
    const formatCentsToDisplay = useCallback((centsValue: number): string => {
      if (centsValue === 0) return '$0.00'

      const isNegative = centsValue < 0
      const absCents = Math.abs(centsValue)

      // Convert cents to dollars with 2 decimals
      const dollars = (absCents / 100).toFixed(2)

      // Split into integer and decimal parts
      const parts = dollars.split('.')

      // Add thousand separators to integer part
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

      const formatted = parts.join('.')
      return isNegative ? `-$${formatted}` : `$${formatted}`
    }, [])

    /**
     * Converts numeric value to cents
     */
    const valueToCents = useCallback(
      (value: number | null | undefined): number => {
        if (value === null || value === undefined) return 0
        return Math.round(value * 100)
      },
      []
    )

    /**
     * Converts cents to numeric value
     */
    const centsToValue = useCallback(
      (centsValue: number): number => centsValue / 100,
      []
    )

    // Where the caret goes once the edited value is on screen: React moves it
    // to the end when it writes a new value.
    const pendingCaret = useRef<number | null>(null)
    // A pointer focus keeps the caret where the click put it; a keyboard
    // focus puts it at the end, ready to type.
    const pointerDown = useRef(false)

    // The selection before the latest change, to read what a change the key
    // handlers did not see (a cut, a keyless keyboard) did to the amount.
    const lastSelection = useRef<{ start: number; end: number } | null>(null)
    const rememberSelection = useCallback(() => {
      const input = inputRef.current
      if (!input) return
      lastSelection.current = {
        start: input.selectionStart ?? input.value.length,
        end: input.selectionEnd ?? input.value.length
      }
    }, [])

    useLayoutEffect(() => {
      if (pendingCaret.current === null || !inputRef.current) return
      inputRef.current.setSelectionRange(
        pendingCaret.current,
        pendingCaret.current
      )
      pendingCaret.current = null
      rememberSelection()
    })

    /**
     * Moves the cursor to the end (right side)
     */
    // A key pressed before the reset runs wins: the reset would otherwise
    // land after it and throw away the caret or selection the key relied on.
    // So does a selection made after the reset was queued, such as a select
    // all right after a focus.
    const resetTimer = useRef<ReturnType<typeof setTimeout>>()
    const resetCursor = useCallback(() => {
      const queued = inputRef.current
      if (queued) {
        const { selectionStart, selectionEnd } = queued
        clearTimeout(resetTimer.current)
        resetTimer.current = setTimeout(() => {
          const el = inputRef.current
          if (
            !el ||
            el.selectionStart !== selectionStart ||
            el.selectionEnd !== selectionEnd
          )
            return
          el.setSelectionRange(el.value.length, el.value.length)
        }, 0)
      }
    }, [])

    /**
     * Handles blur event
     */
    const handleBlur = useCallback(() => {
      const decimalValue = centsToValue(cents)
      if (propOnBlur) {
        propOnBlur(decimalValue === 0 ? null : decimalValue)
      }
    }, [propOnBlur, cents, centsToValue])

    // Bounds apply to what gets typed; clearing the field always reaches zero.
    const clampCents = useCallback(
      (value: number): number => {
        if (value === 0) return 0
        let clamped = value
        if (maxValue !== undefined)
          clamped = Math.min(clamped, Math.round(maxValue * 100))
        if (minValue !== undefined)
          clamped = Math.max(clamped, Math.round(minValue * 100))
        return clamped
      },
      [maxValue, minValue]
    )

    /**
     * Applies a digit, Backspace, Delete or paste to the amount as it was
     * shown, with the selection it had
     */
    const applyEditAt = useCallback(
      (display: string, start: number, end: number, edit: MoneyEdit) => {
        const input = inputRef.current
        if (!input) return
        const result = editMoney(
          display,
          start,
          end,
          edit,
          formatCentsToDisplay,
          clampCents
        )
        if (result.cents === cents) {
          input.setSelectionRange(result.caret, result.caret)
          rememberSelection()
          return
        }
        pendingCaret.current = result.caret
        setCents(result.cents)
        const decimalValue = centsToValue(result.cents)
        if (propOnChange) {
          propOnChange(decimalValue === 0 ? null : decimalValue)
        }
      },
      [
        cents,
        clampCents,
        centsToValue,
        formatCentsToDisplay,
        propOnChange,
        rememberSelection
      ]
    )

    const applyEdit = useCallback(
      (edit: MoneyEdit) => {
        const input = inputRef.current
        if (!input) return
        applyEditAt(
          input.value,
          input.selectionStart ?? input.value.length,
          input.selectionEnd ?? input.value.length,
          edit
        )
      },
      [applyEditAt]
    )

    /**
     * Handles input changes with currency mask (right-to-left)
     */
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value

        // Allow empty input
        if (inputValue === '') {
          setCents(0)
          if (propOnChange) propOnChange(null)
          resetCursor()
          return
        }

        const before = formatCentsToDisplay(cents)
        const { start, end } = lastSelection.current ?? {
          start: before.length,
          end: before.length
        }
        const edit = editFromChange(before, start, end, inputValue)
        if (edit) {
          applyEditAt(before, start, end, edit)
          return
        }

        // Remove all non-digit characters except minus sign
        let cleaned = inputValue.replace(/[^\d-]/g, '')

        // Handle negative sign
        const isNegative = cleaned.startsWith('-') && allowNegative
        cleaned = cleaned.replace(/-/g, '')

        // Only allow digits
        if (!/^\d*$/.test(cleaned)) {
          resetCursor()
          return
        }

        // Parse as cents
        let newCents = parseInt(cleaned || '0', 10)
        if (isNegative) newCents = -newCents

        // Apply max constraint (convert to cents)
        if (maxValue !== undefined) {
          const maxCents = Math.round(maxValue * 100)
          if (newCents > maxCents) newCents = maxCents
        }

        // Apply min constraint (convert to cents)
        if (minValue !== undefined) {
          const minCents = Math.round(minValue * 100)
          if (newCents < minCents) newCents = minCents
        }

        setCents(newCents)

        // Notify parent with decimal value
        const decimalValue = centsToValue(newCents)
        if (propOnChange) {
          propOnChange(decimalValue === 0 ? null : decimalValue)
        }

        // Reset cursor to beginning
        resetCursor()
      },
      [
        propOnChange,
        allowNegative,
        maxValue,
        minValue,
        centsToValue,
        resetCursor,
        cents,
        formatCentsToDisplay,
        applyEditAt
      ]
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        clearTimeout(resetTimer.current)
        rememberSelection()
        // Allow modifier key combos (Cmd+A, Ctrl+C, etc.)
        if (e.metaKey || e.ctrlKey) return

        if (/^\d$/.test(e.key)) {
          e.preventDefault()
          applyEdit({ type: 'insert', text: e.key })
        } else if (e.key === 'Backspace') {
          e.preventDefault()
          applyEdit({ type: 'backspace' })
        } else if (e.key === 'Delete') {
          e.preventDefault()
          applyEdit({ type: 'delete' })
        }
      },
      [applyEdit, rememberSelection]
    )

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        applyEdit({ type: 'paste', text: e.clipboardData.getData('text') })
        propOnPaste?.(e)
      },
      [applyEdit, propOnPaste]
    )

    /**
     * Prevent mouse wheel from changing the value
     */
    const handleWheel = useCallback((e: React.WheelEvent<HTMLInputElement>) => {
      e.currentTarget.blur()
    }, [])

    /**
     * Handle focus - the end of the amount for a keyboard focus or a zero
     * amount
     */
    const handleFocus = useCallback(() => {
      if (!pointerDown.current || cents === 0) resetCursor()
      pointerDown.current = false
    }, [cents, resetCursor])

    /**
     * Handle click - a zero amount is typed from the end, wherever the click
     * landed
     */
    const handleClick = useCallback(() => {
      if (cents === 0) resetCursor()
    }, [cents, resetCursor])

    // Sync with external value changes (controlled mode)
    useEffect(() => {
      if (propValue !== undefined) {
        const numValue =
          typeof propValue === 'string' ? parseFloat(propValue) : propValue
        const newCents = valueToCents(numValue)
        setCents(newCents)
      }
    }, [propValue, valueToCents])

    // Format display value
    const displayValue = formatCentsToDisplay(cents)

    return (
      <Input
        {...props}
        ref={inputRef}
        type='text'
        inputMode='numeric'
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onWheel={handleWheel}
        onPaste={handlePaste}
        onMouseDown={event => {
          pointerDown.current = true
          propOnMouseDown?.(event)
        }}
        onFocus={handleFocus}
        onClick={handleClick}
        onSelect={rememberSelection}
        className={cn('!px-4', props.className)}
      />
    )
  }
)

MoneyInput.displayName = 'MoneyInput'
